import { useCallback, useMemo, useRef, useState } from 'react';

import { conditionsFromParameterList } from '../graph/state/graphModel';

export const CRITERIA_OPERATORS = [
  { value: '=', label: '=' },
  { value: '!=', label: '!=' },
  { value: '<', label: '<' },
  { value: '>', label: '>' },
  { value: '<=', label: '<=' },
  { value: '>=', label: '>=' },
  { value: 'CONTAINS', label: 'CONTAINS' },
  { value: 'IN', label: 'IN' },
];

/**
 * Centralized state and handlers for criteria editor panel + inline rename.
 */
export const useCriteriaEditor = ({ graphState, graphStateRef, applyGraphAction, showFeedback, graphActions }) => {
  const criterionIdRef = useRef(0);

  const [editNodeId, setEditNodeId] = useState(null);
  const [editLabel, setEditLabel] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editStatus, setEditStatus] = useState('unedited');
  const [editIncludeCriteria, setEditIncludeCriteria] = useState([]);
  const [editExcludeCriteria, setEditExcludeCriteria] = useState([]);
  const [editParameterListId, setEditParameterListId] = useState(null);
  const [editParameterListName, setEditParameterListName] = useState('');

  const getNextCriterionId = useCallback(() => {
    criterionIdRef.current += 1;
    return `criterion-${criterionIdRef.current}`;
  }, []);

  const normalizeCriteriaForEdit = useCallback(
    (criteriaArray, fallbackCount = 0, group = 'include') => {
      if (Array.isArray(criteriaArray) && criteriaArray.length > 0) {
        return criteriaArray.map((criterion, index) => {
          if (typeof criterion === 'string') {
            return {
              id: getNextCriterionId(),
              field: criterion,
              operator: '=',
              value: '',
              parameterId: null,
              executable: true,
            };
          }

          if (criterion && typeof criterion === 'object') {
            const field = String(
              criterion.parameterName ||
                criterion.field ||
                criterion.label ||
                criterion.parameter ||
                criterion.name ||
                `Criterion ${index + 1}`,
            );
            const value = String(criterion.value || criterion.term || criterion.targetValue || '');
            const rawOperator = String(criterion.operator || '=').trim();
            const normalizedLookup = rawOperator.toLowerCase();
            const operator =
              normalizedLookup === 'equals'
                ? '='
                : normalizedLookup === 'not_equals'
                  ? '!='
                  : normalizedLookup === 'gte'
                    ? '>='
                    : normalizedLookup === 'lte'
                      ? '<='
                      : normalizedLookup === 'contains'
                        ? 'CONTAINS'
                        : normalizedLookup === 'in'
                          ? 'IN'
                          : rawOperator.toUpperCase();

            return {
              id: getNextCriterionId(),
              field,
              operator: CRITERIA_OPERATORS.some((entry) => entry.value === operator) ? operator : '=',
              value,
              parameterId: Number.isFinite(criterion.parameterId) ? Number(criterion.parameterId) : null,
              executable: criterion.executable !== false,
            };
          }

          return {
            id: getNextCriterionId(),
            field: `Criterion ${index + 1}`,
            operator: '=',
            value: '',
            parameterId: null,
            executable: true,
          };
        });
      }

      const safeCount = Number.isFinite(fallbackCount) ? fallbackCount : 0;
      return Array.from({ length: Math.max(0, safeCount) }, (_, index) => ({
        id: getNextCriterionId(),
        field: `${group === 'include' ? 'Include' : 'Exclude'} criterion ${index + 1}`,
        operator: '=',
        value: '',
        parameterId: null,
        executable: true,
      }));
    },
    [getNextCriterionId],
  );

  const editingNode = useMemo(
    () => (editNodeId ? graphState.nodesById?.[editNodeId] || null : null),
    [editNodeId, graphState.nodesById],
  );

  const closeEdit = useCallback(() => {
    setEditNodeId(null);
    setEditLabel('');
    setEditDescription('');
    setEditStatus('unedited');
    setEditIncludeCriteria([]);
    setEditExcludeCriteria([]);
    setEditParameterListId(null);
    setEditParameterListName('');
  }, []);

  const openEdit = useCallback(
    (nodeId) => {
      const node = graphStateRef.current.nodesById?.[nodeId];
      if (!node) return;

      setEditNodeId(nodeId);
      setEditLabel(node.name || '');
      setEditDescription(node.description || '');
      setEditStatus(node.status || 'unedited');
      setEditIncludeCriteria(normalizeCriteriaForEdit(node.includeConditions, undefined, 'include'));
      setEditExcludeCriteria(normalizeCriteriaForEdit(node.excludeConditions, undefined, 'exclude'));
      setEditParameterListId(node.parameterListId ?? null);
      setEditParameterListName(node.parameterListName || '');
    },
    [graphStateRef, normalizeCriteriaForEdit],
  );

  const renameNode = useCallback(
    (nodeId, nextLabel) => {
      const trimmed = String(nextLabel || '').trim();
      if (!trimmed) return;

      applyGraphAction(
        {
          type: graphActions.UPDATE_NODE,
          payload: {
            nodeId,
            patch: {
              name: trimmed,
            },
          },
        },
        { recordHistory: true },
      );
    },
    [applyGraphAction, graphActions.UPDATE_NODE],
  );

  const addCriterion = useCallback(
    (group) => {
      const nextCriterion = {
        id: getNextCriterionId(),
        field: '',
        operator: '=',
        value: '',
        parameterId: null,
        executable: true,
      };

      if (group === 'include') {
        setEditIncludeCriteria((items) => [...items, nextCriterion]);
      } else {
        setEditExcludeCriteria((items) => [...items, nextCriterion]);
      }
    },
    [getNextCriterionId],
  );

  /**
   * Uebernimmt eine Katalog-Parameterliste als Kriterienquelle (AK1, #779).
   * `parameters` sind bereits aufgeloeste Katalog-Parameter (Name, omopCode,
   * ...) -- die entstehenden Kriterien sind eine Kopie ihres Werts zum
   * Einsatzzeitpunkt, kein Verweis auf die Liste (AK2). Gibt die Anzahl der
   * eingefuegten Kriterien zurueck.
   */
  const insertCriteriaFromParameterList = useCallback(
    (group, list, parameters) => {
      const conditions = conditionsFromParameterList(parameters);
      if (conditions.length === 0) return 0;

      const newCriteria = conditions.map((condition) => ({
        id: getNextCriterionId(),
        field: condition.parameterName,
        operator: condition.operator,
        value: condition.value,
        parameterId: condition.parameterId,
        executable: condition.executable,
      }));

      if (group === 'include') {
        setEditIncludeCriteria((items) => [...items, ...newCriteria]);
      } else {
        setEditExcludeCriteria((items) => [...items, ...newCriteria]);
      }

      setEditParameterListId(list?.id ?? null);
      setEditParameterListName(list?.name || '');

      return newCriteria.length;
    },
    [getNextCriterionId],
  );

  const updateCriterion = useCallback((group, criterionId, key, nextValue) => {
    const updater = (items) =>
      items.map((item) =>
        item.id === criterionId
          ? {
              ...item,
              [key]: nextValue,
            }
          : item,
      );

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const deleteCriterion = useCallback((group, criterionId) => {
    const updater = (items) => items.filter((item) => item.id !== criterionId);

    if (group === 'include') {
      setEditIncludeCriteria(updater);
    } else {
      setEditExcludeCriteria(updater);
    }
  }, []);

  const saveEdit = useCallback(() => {
    if (!editNodeId) return false;

    const nextName = String(editLabel || '').trim();
    if (!nextName) {
      showFeedback('Node label is required.', 'warning');
      return false;
    }

    const normalizeForSave = (items) =>
      items
        .map((item) => ({
          parameterId: Number.isFinite(item.parameterId) ? Number(item.parameterId) : null,
          parameterName: String(item.field || '').trim(),
          operator: String(item.operator || '='),
          value: String(item.value || '').trim(),
          logicalOperator: 'AND',
          executable: item.executable !== false,
        }))
        .filter((item) => item.parameterName.length > 0 || item.value.length > 0);

    const includeConditions = normalizeForSave(editIncludeCriteria);
    const excludeConditions = normalizeForSave(editExcludeCriteria);

    const hasCriteria = includeConditions.length > 0 || excludeConditions.length > 0;
    const resolvedStatus = editStatus === 'unedited' && hasCriteria ? 'edited' : editStatus;
    const executed = resolvedStatus === 'executed' || resolvedStatus === 'completed';
    const currentPatientCount = graphStateRef.current.nodesById?.[editNodeId]?.patientCount;

    const changed = applyGraphAction(
      {
        type: graphActions.UPDATE_NODE,
        payload: {
          nodeId: editNodeId,
          patch: {
            name: nextName,
            description: String(editDescription || '').trim(),
            status: resolvedStatus,
            includeConditions,
            excludeConditions,
            isExecuted: executed,
            patientCount: executed
              ? Number.isFinite(currentPatientCount)
                ? Number(currentPatientCount)
                : 123
              : null,
            parameterListId: editParameterListId,
            parameterListName: editParameterListName,
          },
        },
      },
      { recordHistory: true },
    );

    if (!changed) return false;

    closeEdit();
    showFeedback('Node updated.', 'success');
    return true;
  }, [
    applyGraphAction,
    closeEdit,
    editDescription,
    editExcludeCriteria,
    editIncludeCriteria,
    editLabel,
    editNodeId,
    editParameterListId,
    editParameterListName,
    editStatus,
    graphActions.UPDATE_NODE,
    graphStateRef,
    showFeedback,
  ]);

  return {
    editingNode,
    editNodeId,
    editLabel,
    editDescription,
    editStatus,
    editIncludeCriteria,
    editExcludeCriteria,
    editParameterListName,
    criteriaOperators: CRITERIA_OPERATORS,
    setEditLabel,
    setEditDescription,
    setEditStatus,
    openEdit,
    closeEdit,
    renameNode,
    addCriterion,
    updateCriterion,
    deleteCriterion,
    insertCriteriaFromParameterList,
    saveEdit,
  };
};

export default useCriteriaEditor;
